// #genai
// Provides a stable callback that micro-frontend child apps can call at any time
// to read the current guest / shared-resource context from the container's Redux store.
//
// A callback backed by a ref is used rather than a plain prop so that MFEs mounted
// via mfInstance.mount() (called once) always get the latest values without a re-mount.
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

/**
 * Returns a stable `getGuestContext` callback suitable for passing to micro-frontends.
 *
 * The callback returns a plain object with a clean, versioned contract so child apps
 * are decoupled from the internal Redux slice shape.
 *
 * @returns {() => GuestContext} getGuestContext
 *
 * @typedef {object} GuestContext
 * @property {boolean}      isResourceShared      - Resource is marked as shared on the platform
 * @property {boolean}      isEvUser              - True for Okta/EV employees; false for guest users
 * @property {boolean|null} hasAccess             - checkResourceAccess result: true = access granted, null = not yet checked
 * @property {string}       email                 - Email address (from sign-in form or decoded from token)
 * @property {string}       appCode               - Application code (e.g. "truedesign")
 * @property {string|null}  resourceAccessAuditId - ID from checkResourceAccess response, used for backend status updates
 */
const useGuestContextCallback = () => {
  const guestAccess = useSelector((state) => state.GuestAccess);

  // Keep a ref in sync so the callback always reads latest state.
  const guestAccessRef = useRef(guestAccess);
  useEffect(() => {
    guestAccessRef.current = guestAccess;
  }, [guestAccess]);

  const getGuestContext = useCallback(() => {
    const s = guestAccessRef.current;
    return {
      isResourceShared: s.isResourceShared,
      isEvUser: s.isEvUser,
      hasAccess: s.hasAccess, // null until checkResourceAccess resolves #genai
      email: s.email,
      appCode: s.appCode,
      resourceAccessAuditId: s.resourceAccessAuditId, // null until checkResourceAccess resolves #genai
    };
  }, []);

  return getGuestContext;
};

export default useGuestContextCallback;
